/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { MyPageTicket } from './types';

export type AvailableTicketsPageProps = {
  tickets: MyPageTicket[];
  onTicketClick: (ticketId: number) => void;
  primaryColor: string;
};

export const AvailableTicketsPage: React.FC<AvailableTicketsPageProps> = ({
  tickets,
  onTicketClick,
  primaryColor,
}) => {
  return (
    <>
      {tickets.length === 0 ? (
        <div css={styles.container}>
          <div css={styles.content}>
            <p css={styles.text}>利用可能なチケットはありません</p>
          </div>
        </div>
      ) : (
        <div css={styles.container}>
          <div css={styles.content}>
            <div css={styles.ticketList}>
              {tickets.map((ticket) => {
                return (
                  <div key={ticket.id} css={styles.ticketItemWrapper}>
                    {/* チケットタイプヘッダー */}
                    <div css={styles.ticketHeader(primaryColor)}>
                      <span css={styles.ticketLabel}>{ticket.ticketType}</span>
                    </div>
                    <div
                      css={styles.ticketItem}
                      onClick={() => {
                        onTicketClick(ticket.id);
                      }}
                    >
                      <div css={styles.ticketContent}>
                        <div css={styles.ticketMainInfo}>
                          <div css={styles.ticketTitleRow}>
                            <h3 css={styles.ticketTitle}>
                              {ticket.ticketPlanName || ''}
                            </h3>
                            <ChevronRightIcon css={styles.arrowIcon} />
                          </div>
                          <div css={styles.ticketDetails}>
                            <div css={styles.remainingSection}>
                              <span css={styles.remainingLabel}>
                                残り
                                {ticket.unitSuffix}数
                              </span>
                              <div css={styles.remainingCount}>
                                <span
                                  css={styles.currentCount(
                                    ticket.isLowRemaining,
                                    primaryColor
                                  )}
                                >
                                  {ticket.remainingCount}
                                </span>
                                <span css={styles.countSeparator}>/</span>
                                <span css={styles.totalCount}>
                                  {ticket.totalCount}
                                </span>
                                <span css={styles.unitSuffix}>
                                  {ticket.unitSuffix}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div css={styles.ticketFooter}>
                          <div css={styles.expiryInfo}>
                            <span css={styles.expireDate}>
                              {ticket.expireDate}まで有効
                            </span>
                            {ticket.isExpiringSoon && (
                              <span css={styles.expiringSoonBadge}>
                                期限切れ間近
                              </span>
                            )}
                          </div>
                          {!ticket.isExpiringSoon && (
                            <CheckCircleOutlineIcon css={styles.checkIcon} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  container: css`
    padding: 0 16px 16px;
    min-height: 400px;
  `,
  text: css`
    font-size: 14px;
    color: #666;
    text-align: center;
    margin-top: 20px;
  `,
  content: css`
    font-size: 14px;
    color: #666;
  `,
  ticketList: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  ticketItemWrapper: css`
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    overflow: hidden;
    position: relative;
  `,
  ticketHeader: (primaryColor: string) => css`
    background-color: ${primaryColor};
    padding: 4px 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${primaryColor};
    border-bottom: none;
  `,
  ticketLabel: css`
    color: white;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.17px;
  `,
  ticketItem: css`
    cursor: pointer;
    transition: background-color 0.2s;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    &:hover {
      background-color: #f5f5f5;
    }
  `,
  ticketContent: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  ticketMainInfo: css`
    display: flex;
    flex-direction: column;
  `,
  ticketTitle: css`
    font-size: 12px;
    font-weight: 400;
    color: #333333;
    margin: 0;
    letter-spacing: 0.48px;
  `,
  ticketTitleRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  `,
  ticketDetails: css`
    display: flex;
    align-items: center;
    gap: 16px;
  `,
  remainingSection: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 16px;
    background-color: #f5f5f5;
    border-radius: 8px;
    position: relative;
  `,
  remainingLabel: css`
    font-size: 16px;
    font-weight: 600;
    color: #333333;
    letter-spacing: 0.64px;
  `,
  remainingCount: css`
    display: flex;
    align-items: baseline;
  `,
  currentCount: (isLowRemaining?: boolean, primaryColor?: string) => css`
    font-size: 32px;
    font-weight: 600;
    color: ${isLowRemaining ? '#ff6a00' : primaryColor || '#333333'};
    margin-right: 4px;
    line-height: 1;
  `,
  countSeparator: css`
    font-size: 16px;
    font-weight: 400;
    color: #bababa;
    position: absolute;
    top: 26px;
    right: 34px;
  `,
  totalCount: css`
    font-size: 14px;
    font-weight: 400;
    color: #bababa;
    margin-left: 8px;
  `,
  unitSuffix: css`
    font-size: 10px;
    font-weight: 500;
    color: #bababa;
    position: absolute;
    top: 14px;
    right: 16px;
  `,
  ticketFooter: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  expiryInfo: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  expireDate: css`
    font-size: 12px;
    color: #8d8d8d;
    letter-spacing: 0.48px;
  `,
  expiringSoonBadge: css`
    background-color: #ffd4d4;
    color: #e63a1c;
    padding: 0 6px;
    border-radius: 24px;
    line-height: 20px;
    height: 20px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4px;
  `,
  checkIcon: css`
    color: #00c746;
    font-size: 12px;
  `,
  arrowIcon: css`
    color: #bababa;
    font-size: 24px;
    &.MuiSvgIcon-root {
      font-size: 24px;
    }
  `,
  logo: css`
    position: absolute;
    bottom: 8px;
    right: 16px;
    width: auto;
    height: 20px;
    opacity: 0.5;
    z-index: 1;
  `,
};
